import { Icon } from "@iconify/react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  IconButton,
  Typography
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLikedProducts } from "../contexts/LikedProductsContext";
import { useDispatch,useSelector } from "react-redux";
import { unlikeProduct } from "../../store/reducers/userReducer";

const Likes = () => 
  {
  const dispatch = useDispatch();  
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [likedProducts, setLikedProducts] = useState([]);
  const { removeLikedProduct } = useLikedProducts();

  // Fetch liked products (example API call)
  const fetchLikedProducts = async () => {
    try {
      const response = await axios.post("/get_favorites", {
        token: user.user.token,
      });
      setLikedProducts(
        response.data.filter((product) => product && product.product)
      );
    } catch (error) {
      console.error("Error fetching liked products:", error);
    }
  };

  useEffect(() => {
    fetchLikedProducts();
  }, []);


  const reversedLikedProducts = [...likedProducts].reverse();

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        backgroundColor: "white",
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingBottom: 4,
      }}
    >
      {/* Header Section */}
      <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // Keeps title centered
            width: "100%",
            padding: "16px 32px",
            paddingTop: "60px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 10,
          }}
        >
          {/* Back Button (Left-Aligned) */}
          <IconButton 
            size="medium" 
            sx={{ 
              color: "black", 
              position: "absolute", 
              left: 32 // Ensures back button stays on the left
            }} 
            onClick={() => navigate("/products/")}
          >
            <ArrowBackIosNewIcon fontSize="medium" />
          </IconButton>

          {/* Title (Centered) */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: "#112A3B", 
              textAlign: "center",
            }}
          >
            Liked Products
          </Typography>
        </Box>
  
      {/* Scrollable Product List */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "800px",
          backgroundColor: "white",
          paddingX: 2,
          height: "70vh", // Restrict height to enable scrolling
          overflowY: "auto", // Enables vertical scrolling inside the container
          scrollbarWidth: "thin", // Firefox scrollbar fix
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#bbb",
            borderRadius: "10px",
          },
        }}
      >
        {likedProducts.length > 0 ? (
          reversedLikedProducts.map((product, index) =>
            product.product ? (
              <Card
                key={index}
                sx={{
                  mb: 2,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  transition: "0.2s ease-in-out",
                  "&:hover": { boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.15)" },
                  padding: "15px",
                  height: "130px", // Increased card height to fill more space
                }}
                onClick={() => navigate(`/products/details/${product.product.upc}`)}
              >
                {/* Product Image */}
                <CardMedia
                  component="img"
                  sx={{
                    width: 100, // Increased image size
                    height: 100,
                    borderRadius: 3,
                    objectFit: "contain",
                    marginLeft: 2,
                  }}
                  src={product.product.image
                    ? product.product.image
                    : `https://placehold.co/800x400?text=${product.product.title}`}
                  alt={product.product.title || "No title"}
                />
  
                {/* Product Details */}
                <Box sx={{ flex: 1, paddingX: 3 }}>
                  <CardContent sx={{ padding: "0px" }}>
                    {/* Product Title */}
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        fontSize: "18px",
                        fontWeight: 600,
                      }}
                    >
                      {product.product.title || "Untitled Product"}
                    </Typography>
  
                    {/* Ratings & Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        ⭐ 4.9 (302 Ratings)
                      </Typography>
  
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* Delete Icon */}
                        <IconButton
                          sx={{ transform: "scale(1.1)", color: "red" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLikedProduct(product.product.upc);
                            dispatch(unlikeProduct({product: product.product}));
                            setLikedProducts((prev) =>
                              prev.filter((p) => p.product.upc !== product.product.upc)
                            );
                          }}
                        >
                          <Icon icon="ic:outline-delete" style={{ fontSize: 22 }} />
                        </IconButton>
  
                        {/* Chat Icon */}
                        <IconButton
                          sx={{ transform: "scale(1.1)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/products/chat/${product.product.upc}`);
                          }}
                        >
                          <Icon icon="heroicons:chat-bubble-left-ellipsis" style={{ color: "grey", fontSize: 24 }} />
                        </IconButton>
  
                        {/* Store Icon */}
                        <IconButton
                          sx={{ transform: "scale(1.1)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/products/details/${product.product.upc}`);
                          }}
                        >
                          <Icon icon={"bx:store"} style={{ color: "grey", fontSize: 24 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Box>
              </Card>
            ) : null
          )
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", marginTop: 2 }}>
            No liked products found.
          </Typography>
        )}
      </Box>
    </Container>
  );
  
};

export default Likes;
